import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingUp, Users, CheckCircle2, Clock } from "lucide-react";

const COLORS = ["#1B3B36", "#A3B899", "#C8553D", "#457B9D", "#E07A5F", "#8DA682"];

export default function ReportsPage() {
  const [m, setM] = useState(null);

  useEffect(() => { api.get("/dashboard/metrics").then(({ data }) => setM(data)); }, []);
  if (!m) return <div className="text-[var(--cc-text-soft)]">Loading…</div>;

  const sourceData = Object.entries(m.by_source).map(([name, value]) => ({ name, value }));
  const scoreData = Object.entries(m.score_distribution).map(([name, value]) => ({ name: `${name}/5`, value }));

  return (
    <div className="fade-up">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">Analytics</div>
        <h1 className="font-display text-4xl font-bold">Recruitment Reports</h1>
        <p className="text-[var(--cc-text-soft)] mt-1">Hiring metrics, sourcing performance and interview score distribution.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard icon={Users} label="Total Candidates" value={m.total_candidates} />
        <MetricCard icon={CheckCircle2} label="Hired" value={m.hired} />
        <MetricCard icon={TrendingUp} label="Offer Acceptance" value={`${m.offer_acceptance_rate}%`} />
        <MetricCard
          icon={Clock}
          label="Avg Days to Hire"
          value={m.hired_sample_size > 0 ? `${m.avg_days_to_hire}d` : "—"}
          hint={m.hired_sample_size > 0
            ? `Across ${m.hired_sample_size} hire${m.hired_sample_size === 1 ? "" : "s"} (application → hired)`
            : "No completed hires yet"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="cc-card p-6">
          <h3 className="font-dash font-bold text-lg mb-1">Candidate Sources</h3>
          <p className="text-xs text-[var(--cc-text-soft)] mb-4">Where applications come from</p>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={sourceData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label>
                {sourceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="cc-card p-6">
          <h3 className="font-dash font-bold text-lg mb-1">Interview Score Distribution</h3>
          <p className="text-xs text-[var(--cc-text-soft)] mb-4">Average score per scorecard (1-5 rubric)</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={scoreData}>
              <XAxis dataKey="name" stroke="#4A4D48" />
              <YAxis stroke="#4A4D48" allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#1B3B36" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="cc-card p-6 lg:col-span-2">
          <h3 className="font-dash font-bold text-lg mb-3">Open Jobs</h3>
          <div className="font-dash text-5xl font-bold">{m.open_jobs}</div>
          <p className="text-sm text-[var(--cc-text-soft)] mt-2">Active roles currently accepting applications via the Candidate Portal.</p>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="cc-card p-5">
      <Icon className="w-5 h-5 text-[var(--cc-primary)] mb-3" />
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="font-dash text-3xl font-bold">{value}</div>
      {hint && <div className="text-[11px] text-[var(--cc-text-soft)] mt-1 leading-snug">{hint}</div>}
    </div>
  );
}
